import { useTheme } from '@emotion/react';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
  Checkbox,
  Select,
  Pagination,
  MenuItem,
  Switch,
  alpha,
  Paper,
  Tooltip,
  TableContainer,
  Rating,
  IconButton,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useTable } from 'react-table';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  capitalizedSentence,
  bindShiftWheelScroll,
  containerRef,
  downloadFile,
  getFileCategory,
  hasPermission,
  stringAvatar,
} from '../utilities';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Secondary } from './CustomButton';
import { ReactComponent as PreviousIcon } from '../assets/table-previous.svg';
import { ReactComponent as DownIcon } from '../assets/table-chevron-down.svg';
import { ReactComponent as NextIcon } from '../assets/table-next.svg';
import NoData from './NoData';
import { styled } from '@mui/styles';
import ModalComponent from './ModalComponent';
import { Add, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import LeadInvestorControlPopup from './popups/ConfirmationPopup';
import { EditInvestorPopupPostApi } from '../api/investors';
import ImageIcon from 'common-components/ImageIcon';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import ImageAvatar from './ImageAvatar';
import { useToast } from 'context/ToastContext';

const CustomSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#e0e0e0',
    '&:hover': {
      backgroundColor: alpha('#000000', theme.palette.action.hoverOpacity),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#000000',
  },
}));

// One chip for every status/source value. The per-value colour switch that used
// to live here (blue "Sent", green "Backrr", yellow "Invited", ...) gave the
// tables a different palette from the rest of the app and implied a severity
// these values don't carry - "Self" vs "Backrr" is a source, not a state. This
// is the same chip CustomTable already renders, so the two tables now match.
// Per-status colours (amber/blue/green/red/...), restored from the pre-redesign
// version. The redesign had flattened this to a single lavender chip, which is
// the "the colours disappeared" regression on status cells.
const statusChipStyle = (value) => {
  switch (value) {
    case "Invited": return { color: "#d4a72d", bgcolor: "#fef8e8" };
    case "Added": return { color: "#39739d", bgcolor: "#e1ecf4" };
    case "Interested": return { color: "#1b5e20", bgcolor: "#e8f5e9" };
    case "Rejected": return { color: "#d32f2f", bgcolor: "#ffebee" };
    case "Pending": return { color: "#ff9800", bgcolor: "#fff3e0" };
    case "Completed": return { color: "#673ab7", bgcolor: "#ede7f6" };
    case "Received": return { color: "#00695c", bgcolor: "#e0f2f1" };
    case "Sent": return { color: "#1565c0", bgcolor: "#e3f2fd" };
    case "Backrr": return { color: "#00695c", bgcolor: "#e0f2f1" };
    case "Self": return { color: "#1565c0", bgcolor: "#e3f2fd" };
    default: return { bgcolor: "#F9F5FF", color: "#3538CD" };
  }
};

const StatusChip = ({ cell }) => (
  <Chip
    style={{ borderRadius: '8px' }}
    variant="filled"
    label={cell?.value}
    sx={{ marginInline: 'auto', fontWeight: 600, ...statusChipStyle(cell?.value) }}
  />
);

// Columns that hold row actions rather than a value - rendered in the card
// footer, never as a label/value pair. Mirrors the table's own check, so a card
// and a row agree on what counts as an action column.
const ACTION_COLUMN_IDS = ['actions', 'report_actions', 'row_actions'];

// ─────────────────────────────────────────────────────────────
// Mobile card list
// ─────────────────────────────────────────────────────────────
// A table with 6-11 columns is unusable on a phone: it either clips or forces a
// horizontal scroll, and neither reads well. Below `sm` every listing renders as
// cards instead, built from the SAME react-table rows - so custom Cell renderers
// (avatars, chips, links) and RowActions keep working with no per-table changes.
//
// The card deliberately shows only a few fields. Columns opt out with
// `showInCard: false`, or set `cardPriority` to be picked first; otherwise the
// first few in column order are used, capped by `cardFieldLimit`. Dumping all 11
// columns into a card is just a table with extra steps.
const MobileCardList = ({
  rows,
  prepareRow,
  RowActions,
  CustomLink,
  blurRowsFromIndex,
  cardFieldLimit,
  theme,
  // Opt-in card slots (Deal Flow). When provided, `renderCardMenu(row)` moves the
  // row's menu to the header's top-right, and `renderCardContact(row)` renders a
  // contact control in the bottom-left footer group. Listings that don't pass them
  // keep the default layout (RowActions bottom-right) untouched.
  renderCardMenu,
  renderCardContact,
  // When provided, renders the functional (wired) selection checkbox for the row.
  renderRowCheckbox,
  // When provided, renders a "Select all" control at the top of the card list
  // (the mobile equivalent of the table's header checkbox).
  renderSelectAll,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.25 }}>
    {renderSelectAll && rows?.length > 0 && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0.5,
        }}
      >
        {renderSelectAll()}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: theme.palette.customColors.g500 }}>
          Select all
        </Typography>
      </Box>
    )}
    {rows.map((row, idx) => {
      prepareRow(row);
      const isBlurred = blurRowsFromIndex != null && idx >= blurRowsFromIndex;

      const valueCells = row.cells.filter(
        (cell) =>
          !ACTION_COLUMN_IDS.includes(cell.column.id) &&
          cell.column.id !== 'checkbox' &&
          cell.column.showInCard !== false
      );
      const actionCells = row.cells.filter((cell) =>
        ACTION_COLUMN_IDS.includes(cell.column.id)
      );

      // Columns flagged `cardFooter` are interactive (a Status dropdown, a Links
      // button, ...). They render in the bottom bar next to the row actions instead
      // of as read-only label/value pairs, so every clickable control sits together
      // at the foot of the card. Opt-in: unflagged columns behave exactly as before.
      // Columns flagged `cardIcon` render as compact clickable icons in the card's
      // bottom-right (reel, pitch deck, ...) rather than as label/value pairs.
      const iconCells = valueCells.filter((cell) => cell.column.cardIcon);
      const footerCells = valueCells.filter((cell) => cell.column.cardFooter && !cell.column.cardIcon);
      const bodyCells = valueCells.filter((cell) => !cell.column.cardFooter && !cell.column.cardIcon);

      // Selection checkbox rides in the card header next to the avatar. Detected by
      // its column id (same as the table), so it never becomes a body field or the
      // card's primary heading.
      const checkboxCell = row.cells.find((cell) => cell.column.id === 'checkbox');
      // "-" is the empty marker used across these listings. Treat null/""/"-" as empty
      // so a body field can show its label with a blank value, and empty icons vanish.
      const isEmptyValue = (v) => v == null || v === '' || v === '-';
      // Bottom-right icons render only when they have something to show - no dash.
      const visibleIconCells = iconCells.filter((cell) => !isEmptyValue(cell.value));

      // The "fixed" column is the row's identity (name + avatar) - it heads the
      // card. Everything else becomes a label/value pair beneath.
      const primary =
        bodyCells.find((cell) => cell.column.fixed) || bodyCells[0];
      const details = bodyCells
        .filter((cell) => cell !== primary)
        .sort(
          (a, b) =>
            (b.column.cardPriority ?? 0) - (a.column.cardPriority ?? 0)
        )
        .slice(0, cardFieldLimit);

      const renderValue = (cell) =>
        cell.column.status ? (
          <StatusChip cell={cell} row={row} />
        ) : (
          cell.render('Cell')
        );

      return (
        <Box
          key={row.id}
          sx={{
            border: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
            borderRadius: '12px',
            backgroundColor: '#fff',
            padding: '0.875rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            ...(isBlurred
              ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' }
              : {}),
          }}
        >
          {(primary || renderCardMenu) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                {checkboxCell && (
                  <Box sx={{ flexShrink: 0 }}>
                    {renderRowCheckbox ? (
                      renderRowCheckbox(row, checkboxCell.column)
                    ) : (
                      <Checkbox
                        style={{ height: 20, width: 20, color: 'black' }}
                        sx={{ '& .MuiSvgIcon-root': { borderRadius: '8px' } }}
                      />
                    )}
                  </Box>
                )}
                {primary && (
                <Box sx={{ minWidth: 0, fontWeight: 600, flex: 1 }}>
              <CustomLink row={row}>
                {/* A person/organisation row must show its avatar. The table
                    renders it itself for the `name` column (ImageAvatar + name +
                    email) rather than via a Cell renderer, so `render('Cell')`
                    would give the card the bare string and drop the face. Mirror
                    the table's markup here. */}
                {primary.column.id === 'name' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      minWidth: 0,
                    }}
                  >
                    <ImageAvatar
                      src={
                        row.original?.image ||
                        row.original?.profile_picture ||
                        ''
                      }
                      alt={primary.value}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      {primary.value && (
                        <Typography
                          sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                        >
                          {primary.value}
                        </Typography>
                      )}
                      {!primary.column.hideEmail &&
                        row.original?.email && (
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 400,
                              color: theme.palette.customColors.g100,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {row.original.email}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                ) : (
                  renderValue(primary)
                )}
              </CustomLink>
                </Box>
                )}
              </Box>
              {renderCardMenu && (
                <Box sx={{ flexShrink: 0 }}>{renderCardMenu(row)}</Box>
              )}
            </Box>
          )}

          {details.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '1rem 0.75rem',
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              {details.map((cell) => (
                <Box key={cell.column.id} sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: theme.palette.customColors.g100,
                      mb: '2px',
                    }}
                  >
                    {typeof cell.column.Header === 'string'
                      ? cell.column.Header
                      : ''}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: theme.palette.customColors.g500,
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {/* Keep the label, but show a blank value instead of "-" when empty. */}
                    {isEmptyValue(cell.value) ? null : renderValue(cell)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {(footerCells.length > 0 || visibleIconCells.length > 0 || renderCardContact || (!renderCardMenu && actionCells.length > 0)) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              {/* Left: interactive read-context cells (Status, ...) only. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', minWidth: 0 }}>
                {footerCells.map((cell) => (
                  <Box key={cell.column.id} sx={{ minWidth: 0 }}>
                    {renderValue(cell)}
                  </Box>
                ))}
              </Box>
              {/* Right (right-aligned): contact control, then the icon cells. The icons
                  render in REVERSED column order so the reel sits last - the requested
                  order is message, pitch deck, reel. Row actions follow only when the
                  menu hasn't been hoisted to the header. */}
              {(renderCardContact || visibleIconCells.length > 0 || (!renderCardMenu && actionCells.length > 0)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  {renderCardContact && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {renderCardContact(row)}
                    </Box>
                  )}
                  {[...visibleIconCells].reverse().map((cell) => (
                    <Box key={cell.column.id} sx={{ minWidth: 0 }}>
                      {renderValue(cell)}
                    </Box>
                  ))}
                  {!renderCardMenu && actionCells.length > 0 && (
                    <RowActions row={row} cell={actionCells[0]} />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

const AsyncTableListing = ({
  columns,
  data = [],
  title,
  actions: Actions,
  RowActions = () => {},
  chipText = '',
  emptyTitle = 'No Data',
  CustomLink = ({ children }) => <>{children}</>,
  backgroundColor = '#fff',
  page = 1,
  setPage = () => {},
  limit = 20,
  setLimit = () => {},
  pagination = true,
  count,
  sx,
  tabValue = '',
  refetchListing = () => {},
  tableHeadStyles = {},
  // Optional override for the document_name cell's file icon. Defaults to null so
  // every existing caller keeps the built-in getFileCategory icon unchanged; only
  // callers that pass it (RoundDocuments) render their own.
  renderDocumentIcon = null,
  noDataTitle = 'No Data',
  noDataSubTitle = '',
  noDataPlaceHolderImage = null,
  noDataPlaceHolderSx = {},
  // A rendered empty state (typically <NoDataV2 />). Takes precedence over the
  // legacy noData* props above.
  emptyState = null,
  maxHeight,
  TableFooter = () => {
    return <></>;
  },
  reset = false,
  // Blur every row at or beyond this index (freemium teaser). null = disabled.
  blurRowsFromIndex = null,
  // Below `sm`, render rows as cards instead of a table. Opt out per listing.
  mobileCards = true,
  // When true, an empty result keeps the FULL table shell (bordered container +
  // column header) and shows `emptyState` in the body, instead of replacing the
  // whole table with the empty screen. Opt-in so other listings are unaffected.
  emptyInBody = false,
  // How many label/value pairs a card shows besides its heading. Kept low on
  // purpose - a card crammed with every column is just a table with extra steps.
  cardFieldLimit = 4,
  // Opt-in mobile-card slots (see MobileCardList). renderCardMenu(row) -> header
  // top-right; renderCardContact(row) -> bottom-left footer group. Omitting both
  // keeps the default card layout.
  renderCardMenu,
  renderCardContact,
  // Row selection. When `setSelectedIds` is provided, the header + row checkboxes
  // (and the mobile card checkbox) become functional: they populate `selectedIds`
  // / `selectAll`, which the page uses to reveal its bulk "Actions" button.
  // `selectAll` semantics match the backend: when true, `selectedIds` is the set of
  // EXCLUDED ids. `getSelectionId` returns the id stored per row (the startup _id).
  // Omitting `setSelectedIds` keeps the old decorative checkbox untouched.
  selectedIds,
  setSelectedIds,
  selectAll = false,
  setSelectAll,
  getSelectionId = (row) => row?.original?._id,
  // false: no scroll box of its own - the page's scroller moves the table, so its header and
  // fixed columns stick to that (the grouped Deal Flow list). Default keeps the inner scrollbar.
  ownScroll = true,
  // Optional class per body row (e.g. a row animating out of the grouped Deal Flow list).
  getRowClassName = null,
  // Table customization: columns to hide (e.g. ["Source", "Reel"]).
  // Filtered out before rendering; the table never sees them.
  hidden_columns = [],
  // Opt-in row expander (desktop). renderExpandedRow(row.original) renders the panel under a row;
  // when it is null the table looks exactly as before.
  renderExpandedRow = null,
}) => {
  const ScrollArea = ownScroll ? PerfectScrollbar : 'div';
  // Row ids (react-table) whose expanded panel is open.
  const [expandedRows, setExpandedRows] = React.useState(() => new Set());
  const toggleExpanded = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };


        // When component mounts, read limit from cookies
useEffect(() => {
  const savedLimit = Cookies.get("limit");
  if (savedLimit) {
    setLimit(Number(savedLimit));
  }
}, []);
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  // ── Row selection wiring ──────────────────────────────────────────────
  // Gated: only active when the page passes `setSelectedIds`. Otherwise every
  // checkbox stays the old decorative one (no behaviour change for other listings).
  const selectionEnabled = typeof setSelectedIds === 'function';
  const selSet = selectedIds instanceof Set ? selectedIds : new Set();
  const isRowSelected = (row) => {
    const id = getSelectionId(row);
    // selectAll => selectedIds holds EXCLUSIONS, so "selected" is the inverse.
    return selectAll ? !selSet.has(id) : selSet.has(id);
  };
  const toggleRowSelection = (row) => {
    const id = getSelectionId(row);
    const next = new Set(selSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = (checked) => {
    if (setSelectAll) setSelectAll(checked);
    if (setSelectedIds) setSelectedIds(new Set());
  };
  // Header: fully checked only when "all" with no exclusions; indeterminate when a
  // partial set is selected either way.
  const headerAllChecked = selectAll && selSet.size === 0;
  const headerIndeterminate = selSet.size > 0;
  const renderRowCheckbox = (row, column) => (
    <Checkbox
      checked={isRowSelected(row)}
      disabled={column?.disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); toggleRowSelection(row); }}
      style={{ height: 20, width: 20, color: 'black' }}
      sx={{ '& .MuiSvgIcon-root': { borderRadius: '8px' } }}
    />
  );
  const renderSelectAllCheckbox = () => (
    <Checkbox
      checked={headerAllChecked}
      indeterminate={headerIndeterminate}
      onChange={(e) => toggleSelectAll(e.target.checked)}
      style={{ height: 20, width: 20, color: 'black' }}
      sx={{ '& .MuiSvgIcon-root': { borderRadius: '8px' } }}
    />
  );
  // The checkbox column is added by the page only with the right permission, so the
  // mobile "Select all" bar should appear only when that column is actually present.
  const hasCheckboxColumn =
    Array.isArray(columns) && columns.some((c) => (c?.accessor || c?.id) === 'checkbox');

  // Paging while scrolled halfway down leaves the user mid-list, looking at rows
  // they did not ask for. Send the listing back to the top whenever the page or
  // the page size changes.
  const scrollAreaRef = React.useRef(null);
  useEffect(() => {
    scrollAreaRef.current?.scrollTo({ top: 0 });
  }, [page, limit]);
  const userPermissions = useSelector(
    (state) => state?.permissions?.userPermissions
  );
  const [openAddInvestorModal, setOpenAddInvestorModal] = React.useState(false);
  const [currentRow, setCurrentRow] = React.useState(false);

  const handleAddInvestorClose = () => {
    setOpenAddInvestorModal(false);
    setCurrentRow(null);
  };
  const handleAddInvestorSubmit = () => {
    setOpenAddInvestorModal(false);
    setCurrentRow(null);
  };
  const canPreviousPage = page > 1;
  const canNextPage = page < Math.ceil(count / limit);

  const previousPage = () => {
    if (canPreviousPage) {
      // setPage(page - 1);
      if (searchParams?.get('p')) {
        searchParams.set('p', page - 1);
        setSearchParams(searchParams);
      } else {
        searchParams.append('p', page - 1);
        setSearchParams(searchParams);
      }
    }
  };

  const nextPage = () => {
    if (canNextPage) {
      // setPage(page + 1);
      if (searchParams?.get('p')) {
        searchParams.set('p', page + 1);
        setSearchParams(searchParams);
      } else {
        searchParams.append('p', page + 1);
        setSearchParams(searchParams);
      }
    }
  };

  // Filter columns based on table customization (hidden_columns list).
  // Each column has a Header or id; match against the id property.
  const visibleColumns = React.useMemo(() => {
    if (!hidden_columns || hidden_columns.length === 0) return columns;
    return columns.filter((col) => !hidden_columns.includes(col.id || col.accessor));
  }, [columns, hidden_columns]);

  // Columns the table is hiding. The expander only shows these, so it never repeats the row.
  const expandableColumns = React.useMemo(
    () => (columns || []).filter((col) => !visibleColumns.includes(col) && !['checkbox', 'actions'].includes(col.id || col.accessor)),
    [columns, visibleColumns]
  );
  const expandable = Boolean(renderExpandedRow) && expandableColumns.length > 0;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows, // Full data without pagination
    prepareRow,
  } = useTable({
    columns: visibleColumns,
    data: Array.isArray(data) ? data : [],
    initialState: { pageIndex: 0 },
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletScreenOrBelow = useMediaQuery(theme.breakpoints.down('md'));

  const DocumentIcon = ({ row, value }) => {
    if (renderDocumentIcon) return renderDocumentIcon({ row, value });
    return (
      <img src={getFileCategory(value)} width={40} height={40} alt="file-type" style={{ flexShrink: 0 }} />
    );
  };

  const CustomToggle = ({ cell, row }) => {
    // console.log(cell, row, "cell, row")
    const { status, ...currentRow } = row?.original;
    const round_id = Cookies.get('round_id');
    // const { id: round_id } = useParams();
    const currentColumn = cell.column.id;

    const {
      mutateAsync: editInvestorAsync,
      isLoading: isUpdatingInvestor,
      status: editInvestorStatus,
      error: editInvestorError,
      data: editInvestorResponse,
    } = EditInvestorPopupPostApi();

    // Construct details object dynamically based on the column being updated

    const [openModal, setOpenModal] = React.useState(false);

    const handleClose = () => {
      setOpenModal(false);
    };

    const handleSubmission = async () => {
      // currentRow[currentColumn] = !(cell.value);
      const payload = {
        // "first_name": values?.first_name,
        // "last_name": values?.last_name,
        // "email": values?.email,
        // "organization_name": values?.organization_name,
        // "investor_type": values?.investor_type,
        pipeline_id: currentRow?.pipeline_id,
        // "interested_amount": Number(String(currentRow?.interested_amount)?.replaceAll(",", "") != "null" && String(currentRow?.interested_amount)?.replaceAll(",", "") != "undefined" && String(currentRow?.interested_amount)?.replaceAll(",", "")?.length > 0 ? String(currentRow?.interested_amount)?.replaceAll(",", "") : "0"),
        // "allocated_amount": Number(String(currentRow?.allocated_amount)?.replaceAll(",", "") != "null" && String(currentRow?.allocated_amount)?.replaceAll(",", "") != "undefined" && String(currentRow?.allocated_amount)?.replaceAll(",", "")?.length > 0 ? String(currentRow?.allocated_amount)?.replaceAll(",", "") : "0"),
        // "currency_id": currentRow?.currency_id,
        lead_investor: currentRow?.lead_investor || false,
        kyc: currentRow?.kyc || false,
        agreement_signed: currentRow?.agreement_signed || false,
        call_for_money: currentRow?.call_for_money || false,
        fund_received: currentRow?.fund_received || false,
        update_in_progress_date: false,
      };
      payload[currentColumn] = !cell.value;
      // console.log(payload, "payload", currentRow, currentColumn)
      await editInvestorAsync({ payload: { ...payload } }).then(
        (successResponse) => {
          // if (successResponse?.data?.statusCode !== 200) {
          //     // toast.error(successResponse?.data?.message)
          //     addToast(successResponse?.data?.message, "error")
          // }
          // else {
          //     // toast.success("Investor Updated")
          //     addToast("Investor Updated", "success")
          // }
          refetchListing();
          // handleClose()
        }
      );
    };

    const shouldRenderModal = [
      'lead_investor',
      'kyc',
      'agreement_signed',
      'call_for_money',
      'fund_received',
    ].includes(currentColumn);

    return (
      <>
        {/* {shouldRenderModal && (
                    <LeadInvestorControlPopup
                        open={openModal}
                        handleClose={handleClose}
                        handleSubmission={handleSubmission}
                        heading={`Update ${capitalizedSentence(currentColumn.replaceAll("_", " "))} Status`}
                        ActionText="Confirm"
                        subheading={`Are you sure you want to update ${currentColumn.replaceAll("_", " ")} status?`}
                    />
                )} */}
        <CustomSwitch
          // disabled={!hasPermission("funding_rounds", "edit", userPermissions)}
          checked={cell.value}
          cell={cell}
          row={currentRow}
          onChange={(e) => {
            handleSubmission(e);
            // console.log(e, "e")
          }}
          // onChange={handleSubmission}
          // onClick={() => setOpenModal(true)}
        />
      </>
    );
  };

  React.useEffect(() => {
    if (pagination) {
      if (searchParams?.get('p') && Number(searchParams?.get('p'))) {
        if (Number(searchParams?.get('p')) > Math.ceil(count / limit)) {
          setPage(1);
          searchParams.delete('p');
          setSearchParams(searchParams);
        } else {
          // console.log(Number(searchParams?.get('p')), "New")
          setPage(Number(searchParams?.get('p')) || 1);
        }
      }
      // else {
      //     searchParams.append('p', 1);
      //     setSearchParams(searchParams)
      // }
    }
  }, [page, searchParams.get('p')]);

  // React.useEffect(() => {
  //     // setPage(1);
  //     if
  //     searchParams.set()
  //     // console.log(Number(searchParams?.get('p')))
  // }, [reset])

  return (
    <>
      {(data?.length > 0 ) ? (
        <Grid
          container
          xs={12}
          className="table-container"
          border={`1px solid #EAEAEE`}
          borderRadius={sx ? sx?.borderRadius : '16px'}
          boxShadow={'0px 2px 12px 0px rgba(15, 23, 41, 0.04)'}
          bgcolor={backgroundColor}
          maxHeight={
            pagination && count > (limit || 10)
              ? 'calc(100dvh - 15rem)'
              : 'calc(100dvh - 11rem)'
          }
          height={'100%'}
          overflow={'hidden'}
          sx={{ ...sx }}
        >
          {title && (
            <Grid
              container
              item
              xs={12}
              display={'flex'}
              justifyContent={'space-between'}
              padding={'0.5rem'}
              alignItems={'center'}
            >
              <Box display={'flex'} alignItems={'center'} gap={'0.5rem'}>
                <Typography
                  color={theme.palette.customColors.g300}
                  sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    lineHeight: '28px',
                    textAlign: 'left',
                  }}
                >
                  {title}
                </Typography>
                {chipText && (
                  <Chip
                    label={chipText}
                    size="small"
                    sx={{
                      padding: '0.125rem 0.5rem !important',
                      color: '#6941C6',
                      bgcolor: '#F9F5FF',
                      '& > .MuiChip-label': { padding: 0 },
                    }}
                  />
                )}
              </Box>
              {Actions && <Actions />}
            </Grid>
          )}

          {/* <PerfectScrollbar containerRef={containerRef} style={{ height: '100%', maxHeight: matchesUpMd ? '44dvh' : '40dvh', width: '100%' }}> */}
          <ScrollArea
            {...(ownScroll ? {} : { style: { width: '100%' } })}
            {...(ownScroll && { containerRef: (el) => {
              // Keep the existing PerfectScrollbar fix-up, and hold on to the node
              // so a page/limit change can scroll it back to the top.
              containerRef(el);
              bindShiftWheelScroll(el);
              scrollAreaRef.current = el;
            } })}
            {...(ownScroll && { style: {
              height: '100%',
              maxHeight: maxHeight
                ? maxHeight
                : pagination && count > (limit || 10)
                ? matchesUpMd
                  ? 'calc(100dvh - 19.6rem)'
                  : 'calc(100dvh - 19.6rem)'
                : '100%',
              width: '100%',
            } })}
          >
            {/* <TableContainer sx={{ height: '100%', maxHeight: matchesUpMd ? '100%' : '100%', width: '100%' }}> */}
            {isSmallScreen && mobileCards ? (
              emptyInBody && rows?.length === 0 ? (
                emptyState
              ) : (
              <Box
                sx={
                  pagination && count > (limit || 10)
                    ? { marginBottom: '4.4375rem' }
                    : undefined
                }
              >
                <MobileCardList
                  rows={rows}
                  prepareRow={prepareRow}
                  RowActions={RowActions}
                  CustomLink={CustomLink}
                  blurRowsFromIndex={blurRowsFromIndex}
                  cardFieldLimit={cardFieldLimit}
                  theme={theme}
                  renderCardMenu={renderCardMenu}
                  renderCardContact={renderCardContact}
                  renderRowCheckbox={selectionEnabled ? renderRowCheckbox : undefined}
                  renderSelectAll={selectionEnabled && hasCheckboxColumn ? renderSelectAllCheckbox : undefined}
                />
              </Box>
              )
            ) : (
            <table
              style={{
                width: '100%',
                ...(pagination && count > (limit || 10)
                  ? { marginBottom: '4.4375rem' }
                  : {}),
              }}
              {...getTableProps()}
            >
              <thead
                style={{
                  position: 'sticky',
                  zIndex: 1000,
                  top: 0,
                  backgroundColor: theme.palette.background.default,
                  ...tableHeadStyles,
                }}
              >
                {headerGroups?.map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      textAlign: 'left',
                      color: theme.palette.customColors.g100,
                    }}
                    {...headerGroup.getHeaderGroupProps()}
                  >
                    {headerGroup?.headers?.map((column, index) => {
                      const isFixed = column.fixed && !isTabletScreenOrBelow;
                      if (column?.id === 'checkbox') {
                        return (
                          <th
                            style={{
                              minWidth: column?.minWidth,
                              textAlign: column?.align,
                              position: isFixed ? 'sticky' : 'initial',
                              left: isFixed ? 0 : '',
                              zIndex: isFixed ? 1 : 0,
                            }}
                            align={column?.align}
                            key={column.id}
                            {...column.getHeaderProps()}
                          >
                            {selectionEnabled ? (
                              <Checkbox
                                checked={headerAllChecked}
                                indeterminate={headerIndeterminate}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                style={{ height: 20, width: 20, color: 'black' }}
                              />
                            ) : (
                              <Checkbox
                                style={{
                                  height: 20,
                                  width: 20,
                                  color: 'black',
                                }}
                              />
                            )}
                          </th>
                        );
                      } else if (column?.id === 'report_actions' && count > 2) {
                        return (
                          <th
                            style={{
                              minWidth: column?.minWidth,
                              textAlign: column?.align,
                              position: isFixed ? 'sticky' : 'initial',
                              left: isFixed ? 0 : '',
                              zIndex: isFixed ? 1 : 0,
                            }}
                            align={column?.align}
                            key={column.id}
                            {...column.getHeaderProps()}
                          >
                            {data?.length > 0 && (
                              <Typography
                                onClick={() => {
                                  sessionStorage.setItem(
                                    'report_id',
                                    data[0]?.report_id
                                  );
                                  navigate(`/reports/edit_report`);
                                }}
                                sx={{
                                  textAlign: 'center',
                                  color: theme.palette.customColors.indigo600,
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                }}
                              >
                                View All ({count})
                              </Typography>
                            )}
                          </th>
                        );
                      }
                      return (
                        <th
                          style={{
                            minWidth: column?.minWidth,
                            textAlign: column?.align,
                            position: isFixed ? 'sticky' : 'initial',
                            left: isFixed ? 0 : '',
                            zIndex: isFixed ? 1 : 0,
                          }}
                          align={column?.align}
                          key={column.id}
                          {...column.getHeaderProps()}
                        >
                          {column.render('Header')}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows?.length > 0 &&
                  rows?.map((row, idx) => {
                    prepareRow(row);
                    const isBlurred =
                      blurRowsFromIndex != null && idx >= blurRowsFromIndex;
                    const rowProps = row.getRowProps();
                    const isExpanded = expandedRows.has(row.id);
                    return (
                      <React.Fragment key={row.id}>
                      <tr
                        {...rowProps}
                        {...(getRowClassName ? { className: getRowClassName(row.original) || undefined } : {})}
                        style={{
                          ...rowProps.style,
                          ...(isBlurred
                            ? {
                                filter: 'blur(5px)',
                                pointerEvents: 'none',
                                userSelect: 'none',
                              }
                            : {}),
                        }}
                      >
                        {row?.cells?.map((cell, index) => {
                          const isFixed = cell.column.fixed && !isTabletScreenOrBelow;
                          if (cell.column?.id === 'checkbox') {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                }}
                                align={cell.column?.align}
                              >
                                {selectionEnabled ? (
                                  renderRowCheckbox(row, cell.column)
                                ) : (
                                  <Checkbox
                                    style={{
                                      height: 20,
                                      width: 20,
                                      color: 'black',
                                    }}
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        borderRadius: '8px', // Change the border-radius value here
                                      },
                                    }}
                                  />
                                )}
                              </td>
                            );
                          }
                          if (cell.column.id === 'name') {
                            {
                              /* console.log(cell.column?.link, "cell.column?.link") */
                            }
                            {
                              /* console.log(cell.row.original.image || cell.row.original?.profile_picture,"efef") */
                            }
                            if (CustomLink) {
                              return (
                                <td
                                  key={index}
                                  {...cell.getCellProps()}
                                  style={{
                                    textAlign: cell.column?.align,
                                    minWidth: cell.column.minWidth,
                                    position: isFixed
                                      ? 'sticky'
                                      : 'initial',
                                    left: isFixed ? 0 : '',
                                    zIndex: isFixed ? 1 : 0,
                                  }}
                                  align={cell.column?.align}
                                >
                                  <CustomLink row={row} cell={cell}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: theme.palette.customColors.g500,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        lineHeight: '20px',
                                        gap: '12px',
                                        cursor: cell.column?.link
                                          ? cell.column.link === false
                                            ? 'default'
                                            : 'pointer'
                                          : 'default',
                                      }}
                                    >
                                      {/* {console.log(cell.row.original.image)} */}
                                      <ImageAvatar
                                        src={cell.row.original.image || ''}
                                        alt={cell.value}
                                      />
                                      {/* {cell.row.original.image ? <ImageIcon src={cell.row.original.image} style={{ borderRadius: '4px' }} alt={""} /> : <Avatar className='avatar-investor' sx={{}}  {...stringAvatar(cell.value)} />} */}
                                      <Box>
                                        {cell?.value && cell?.value != '' && (
                                          <Typography
                                            fontWeight={600}
                                            sx={{ textWrapMode: 'nowrap' }}
                                          >
                                            {cell.value}
                                          </Typography>
                                        )}
                                        {!cell?.column?.hideEmail && (
                                          <Typography
                                            fontSize={
                                              cell?.value && cell?.value != ''
                                                ? '0.7rem'
                                                : '0.9rem'
                                            }
                                          >
                                            {cell.row.original.email}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </CustomLink>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                    cursor: cell.row.original.public_profile_url
                                      ? 'pointer'
                                      : 'default',
                                  }}
                                  onClick={() => {
                                    cell.row.original.public_profile_url &&
                                      window.open(
                                        cell.row.original.public_profile_url
                                      );
                                  }}
                                >
                                  {/* {cell.row.original.image || cell.row.original?.profile_picture ? <img src={cell.row.original.image || cell.row.original?.profile_picture} style={{ width: 40, height: 40, background: "#F9F5FF", borderRadius: '4px', objectFit: 'contain' }} /> : <Avatar className='avatar-investor' {...stringAvatar(cell.value)} />} */}
                                  <ImageAvatar
                                    src={cell.row.original.image || ''}
                                    alt={cell.value}
                                  />
                                  <Box>
                                    {cell?.value && cell?.value != '' && (
                                      <Typography
                                        fontWeight={
                                          cell.row.original
                                            .public_profile_url && 700
                                        }
                                      >
                                        {cell.value}
                                      </Typography>
                                    )}
                                    {!cell?.column?.hideEmail && (
                                      <Typography
                                        fontSize={
                                          cell?.value && cell?.value != ''
                                            ? '0.7rem'
                                            : '0.9rem'
                                        }
                                      >
                                        {cell.row.original.email}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </td>
                            );
                          } else if (cell.column.id === 'document_name') {
                            const extension = cell?.row?.original?.document_name
                              ?.split('.')
                              ?.pop();
                            if (cell.column?.trim) {
                              if (CustomLink) {
                                return (
                                  <td
                                    key={index}
                                    {...cell.getCellProps()}
                                    style={{
                                      textAlign: cell.column?.align,
                                      minWidth: cell.column.minWidth,
                                      position: isFixed
                                        ? 'sticky'
                                        : 'initial',
                                      left: isFixed ? 0 : '',
                                      zIndex: isFixed ? 1 : 0,
                                    }}
                                    align={cell.column?.align}
                                  >
                                    <CustomLink row={row} cell={cell}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          color:
                                            theme.palette.customColors.g500,
                                          fontSize: '14px',
                                          fontWeight: 500,
                                          lineHeight: '20px',
                                          gap: '12px',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <DocumentIcon
                                          value={extension}
                                          row={row}
                                        />
                                        <Tooltip title={String(cell.value)}>
                                          {String(cell.value)?.length >
                                          cell.column?.trim
                                            ? String(cell.value)?.substring(
                                                0,
                                                cell.column?.trim
                                              ) + '...'
                                            : String(cell.value)}
                                        </Tooltip>
                                      </Box>
                                    </CustomLink>
                                  </td>
                                );
                              }
                              return (
                                <td
                                  key={index}
                                  {...cell.getCellProps()}
                                  style={{
                                    textAlign: cell.column?.align,
                                    minWidth: cell.column.minWidth,
                                    position: isFixed
                                      ? 'sticky'
                                      : 'initial',
                                    left: isFixed ? 0 : '',
                                    zIndex: isFixed ? 1 : 0,
                                  }}
                                  align={cell.column?.align}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: theme.palette.customColors.g500,
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      lineHeight: '20px',
                                      gap: '12px',
                                    }}
                                  >
                                    <DocumentIcon value={extension} row={row} />
                                    <Tooltip title={String(cell.value)}>
                                      {String(cell.value)?.length >
                                      cell.column?.trim
                                        ? String(cell.value)?.substring(
                                            0,
                                            cell.column?.trim
                                          ) + '...'
                                        : String(cell.value)}
                                    </Tooltip>
                                  </Box>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  onClick={() =>
                                    downloadFile(
                                      cell?.row?.original?.document_url
                                    )
                                  }
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <DocumentIcon value={extension} row={row} />{' '}
                                  {String(cell.value)?.substring(0, 50)}
                                </Box>
                              </td>
                            );
                          } else if (cell.column.id === 'filename') {
                            const extension = cell?.row?.original?.filename
                              ?.split('.')
                              ?.pop();
                            if (cell.column?.trim) {
                              return (
                                <td
                                  key={index}
                                  {...cell.getCellProps()}
                                  style={{
                                    textAlign: cell.column?.align,
                                    minWidth: cell.column.minWidth,
                                    position: isFixed
                                      ? 'sticky'
                                      : 'initial',
                                    left: isFixed ? 0 : '',
                                    zIndex: isFixed ? 1 : 0,
                                  }}
                                  align={cell.column?.align}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: theme.palette.customColors.g500,
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      lineHeight: '20px',
                                      gap: '12px',
                                    }}
                                  >
                                    <DocumentIcon
                                      value={
                                        String(cell.value)?.substring(
                                          0,
                                          cell.column?.trim
                                        ) +
                                        '.' +
                                        extension
                                      }
                                      row={row}
                                    />{' '}
                                    {cell.render('Cell')}
                                  </Box>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  <DocumentIcon
                                    value={
                                      String(cell.value)?.substring(
                                        0,
                                        cell.column?.trim
                                      ) +
                                      '.' +
                                      extension
                                    }
                                    row={row}
                                  />{' '}
                                  {cell.render('Cell')}
                                </Box>
                              </td>
                            );
                          } else if (
                            cell.column.id === 'actions' ||
                            cell.column.id === 'report_actions'
                          ) {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent:
                                      cell.column?.align || 'start',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  {expandable && (
                                    <IconButton
                                      size="small"
                                      aria-label={expandedRows.has(row.id) ? 'Collapse row' : 'Expand row'}
                                      aria-expanded={expandedRows.has(row.id)}
                                      onClick={() => toggleExpanded(row.id)}
                                      sx={{ color: '#475467' }}
                                    >
                                      {expandedRows.has(row.id) ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                    </IconButton>
                                  )}
                                  <RowActions cell={cell} row={row} />
                                </Box>
                              </td>
                            );
                          } else if (
                            cell.column.id === 'status' ||
                            cell.column.id === 'pipeline_status' ||
                            cell.column.clickable ||
                            cell.column.status
                          ) {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  <StatusChip cell={cell} row={row} />
                                </Box>
                              </td>
                            );
                          } else if (
                            [
                              'problem',
                              'market',
                              'product',
                              'team',
                              'traction',
                            ]?.includes(cell.column.id)
                          ) {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  <Rating
                                    value={cell?.value}
                                    precision={0.5}
                                    readOnly
                                  />
                                </Box>
                              </td>
                            );
                          } else if (cell.column.toggle) {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: cell.column?.align,
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  <CustomToggle
                                    cell={cell}
                                    row={row}
                                    checked={cell.value}
                                  />
                                </Box>
                              </td>
                            );
                          }
                          if (cell.column.id === 'statusWithDate') {
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Typography>{cell.value}</Typography>
                              </td>
                            );
                          }
                          if (
                            cell.column.id === 'interested_amount_ref' ||
                            cell.column.id === 'allocated_amount_ref' ||
                            cell.column.id === 'investment_amount'
                          ) {
                            const tooltipField = cell?.column?.tooltip;
                            const removePrefixSuffix = (str) => {
                              if (str.startsWith('formatted_')) {
                                return str.slice(10); // Remove "formatted_" (10 characters)
                              }
                              if (str.endsWith('_ref')) {
                                return str.slice(0, -4); // Remove "_ref" (4 characters)
                              }
                              return str;
                            };
                            const currentValue =
                              row.original[removePrefixSuffix(cell.column.id)];
                            return (
                              <td
                                key={index}
                                {...cell.getCellProps()}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    alignItems: 'center',
                                    color: theme.palette.customColors.g500,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    gap: '12px',
                                  }}
                                >
                                  {cell.row?.original?.status != 'Invited' &&
                                  cell?.value != '' &&
                                  cell?.value &&
                                  cell?.value != null &&
                                  currentValue != '0' &&
                                  String(cell?.value)?.trim()?.length !== 0 ? (
                                    <Tooltip
                                      title={row?.original[tooltipField]}
                                    >
                                      <Box>
                                        <Typography>{cell.value}</Typography>
                                        {!cell.column.hideDate && (
                                          <Typography fontSize={'0.7rem'}>
                                            {cell.row.original.date}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <Box
                                      onClick={() => {
                                        if (
                                          hasPermission(
                                            'funding_rounds',
                                            'edit',
                                            userPermissions
                                          )
                                        ) {
                                          setOpenAddInvestorModal(true);
                                          setCurrentRow(cell.row?.original);
                                        }
                                      }}
                                      display={'flex'}
                                      alignItems={'center'}
                                      justifyContent={'center'}
                                      sx={{
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '5px',
                                        textDecorationColor: hasPermission(
                                          'funding_rounds',
                                          'edit',
                                          userPermissions
                                        )
                                          ? '#000000'
                                          : 'rgba(0, 0, 0, 0.26)',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <Add
                                        style={{
                                          width: 20,
                                          height: 20,
                                          color: hasPermission(
                                            'funding_rounds',
                                            'edit',
                                            userPermissions
                                          )
                                            ? '#000000'
                                            : 'rgba(0, 0, 0, 0.26)',
                                        }}
                                      />
                                      <Typography
                                        fontSize={'0.9rem'}
                                        color={
                                          hasPermission(
                                            'funding_rounds',
                                            'edit',
                                            userPermissions
                                          )
                                            ? '#000000'
                                            : 'rgba(0, 0, 0, 0.26)'
                                        }
                                      >
                                        {' '}
                                        Add{' '}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </td>
                            );
                          }

                          if (cell?.column?.tooltip) {
                            const tooltipField = cell?.column?.tooltip;
                            if (
                              cell?.value == undefined ||
                              cell?.value == null ||
                              String(cell?.value)?.trim()?.length === 0
                            ) {
                              return (
                                <td
                                  key={index}
                                  style={{
                                    textAlign: cell.column?.align,
                                    minWidth: cell.column.minWidth,
                                    position: isFixed
                                      ? 'sticky'
                                      : 'initial',
                                    left: isFixed ? 0 : '',
                                    zIndex: isFixed ? 1 : 0,
                                  }}
                                  align={cell.column?.align}
                                  {...cell.getCellProps()}
                                >
                                  -
                                </td>
                              );
                            }
                            return (
                              <td
                                key={index}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                                {...cell.getCellProps()}
                              >
                                <Tooltip title={row?.original[tooltipField]}>
                                  {cell.value}
                                </Tooltip>
                              </td>
                            );
                          }
                          if (
                            cell?.value == undefined ||
                            cell?.value == null ||
                            String(cell?.value)?.trim()?.length === 0
                          ) {
                            return (
                              <td
                                key={index}
                                style={{
                                  textAlign: cell.column?.align,
                                  minWidth: cell.column.minWidth,
                                  position: isFixed
                                    ? 'sticky'
                                    : 'initial',
                                  left: isFixed ? 0 : '',
                                  zIndex: isFixed ? 1 : 0,
                                }}
                                align={cell.column?.align}
                                {...cell.getCellProps()}
                              >
                                {/* {cell.render('Cell')} */}-
                              </td>
                            );
                          }

                          return (
                            <td
                              key={index}
                              style={{
                                textAlign: cell.column?.align,
                                minWidth: cell.column.minWidth,
                                position: isFixed
                                  ? 'sticky'
                                  : 'initial',
                                left: isFixed ? 0 : '',
                                zIndex: isFixed ? 1 : 0,
                              }}
                              align={cell.column?.align}
                              {...cell.getCellProps()}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                      {expandable && isExpanded && (
                        <tr>
                          <td colSpan={visibleColumns.length} style={{ padding: 0, background: '#FAFAFA' }}>
                            {renderExpandedRow(row.original, expandableColumns)}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                {/* emptyInBody: keep the header + container, drop the empty screen into
                    the body spanning every column, so an empty result reads as the same
                    table minus its rows. */}
                {emptyInBody && rows?.length === 0 && (
                  <tr>
                    <td colSpan={columns?.length > 0 ? columns?.length : 1} style={{ padding: 0 }}>
                      {emptyState}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
            {/* </TableContainer> */}
          </ScrollArea>
          <TableFooter />
          {/* Shown whenever there is more than one page - for the mobile card list
              as much as for the table, since this sits outside the scroll area and
              is a sibling of both. The check used to be a hardcoded `count > 10`,
              which was wrong in both directions: with limit=5 and 8 rows it hid a
              pager that was needed, and with limit=25 and 20 rows it showed one
              for a single page. */}
          {pagination && count > (limit || 10) && (
            <Grid
              container
              item
              xs={12}
              sx={{
                padding: '12px 24px 16px 24px',
                display: 'grid',
                gridTemplateAreas: isSmallScreen
                  ? `"previous number next"`
                  : `"previous number page span next"`,
                gridTemplateColumns: isSmallScreen
                  ? '1fr 2fr 1fr'
                  : '1fr 1fr 10fr 1fr 1fr',
                gridTemplateRows: isSmallScreen ? '1fr' : '1fr',
                borderTop: `1px solid ${theme.palette.customColors.gray200}`,
                position: 'sticky',
                bottom: '-1px',
                background: theme.palette.customColors.whiteBG,
                rowGap: 1,
                columnGap: 1,
                alignItems: 'center',
                zIndex: 1000,
              }}
            >
              <Grid item display={'flex'} gap={'0.5rem'} gridArea={'previous'}>
                <Secondary
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  sx={{
                    minWidth: '0 !important',
                    border: `1px solid #D0D5DD !important`,
                    padding: '8px 14px',
                    '& > .MuiButton-icon.MuiButton-startIcon': {
                      marginRight: isSmallScreen ? 0 : '8px',
                      marginLeft: isSmallScreen ? 0 : '-4px',
                    },
                  }}
                  startIcon={
                    <PreviousIcon
                      stroke={!canPreviousPage ? '#D0D5DD' : '#344054'}
                    />
                  }
                >
                  {isSmallScreen ? '' : 'Previous'}
                </Secondary>
              </Grid>
              <Grid
                item
                gridArea={'number'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Select
                  value={limit}
                  onChange={(event) => {
                    // setLimit(event.target.value);
                    // setPage(1);

                    const newLimit = event.target.value;
                    setLimit(newLimit);
                    Cookies.set('limit', newLimit); // ✅ persist limit
                    setPage(1);
                  }}
                  size="small"
                  IconComponent={DownIcon}
                  sx={{
                    minWidth: 150, // Adjust the width as needed
                    borderRadius: '8px',
                    border: '1px solid #D0D5DD', // Border similar to the one in the image
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#344054', // Text color
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSvgIcon-root': {
                      marginTop: 0,
                      color: '#344054', // Arrow icon color
                    },
                    '& > .MuiSelect-icon': {
                      top: '25%',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none', // Remove the default border
                    },
                  }}
                >
                  {[10, 20, 25, 30, 40, 50]?.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size} Per page
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              {!isSmallScreen && (
                <Grid
                  item
                  display={'flex'}
                  justifyContent={'center'}
                  gridArea={'page'}
                >
                  <Pagination
                    count={Math.ceil(count / limit)}
                    page={page}
                    onChange={(e, value) => {
                      // setPage(value);
                      if (searchParams.get('p')) {
                        searchParams.set('p', value);
                        setSearchParams(searchParams);
                      } else {
                        searchParams.append('p', value);
                        setSearchParams(searchParams);
                      }
                    }}
                    shape="rounded"
                    hideNextButton
                    hidePrevButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#6c757d', // Inactive page color
                        fontWeight: '500',
                      },
                      '& .MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: 'rgba(138, 75, 244, 0.1)', // Light purple background
                        color: '#8A4BF4', // Active page color
                      },
                      '& .MuiPaginationItem-ellipsis': {
                        color: '#6c757d', // Ellipsis color
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                gridArea={'number'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              ></Grid>
              <Grid
                item
                display={'flex'}
                flexDirection={'row-reverse'}
                gridArea={'next'}
              >
                <Secondary
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  sx={{
                    minWidth: '0 !important',
                    border: `1px solid #D0D5DD !important`,
                    padding: '8px 14px',
                    '& > .MuiButton-icon.MuiButton-endIcon': {
                      marginRight: isSmallScreen ? 0 : '-4px',
                      marginLeft: isSmallScreen ? 0 : '8px',
                    },
                  }}
                  endIcon={
                    <NextIcon stroke={!canNextPage ? '#D0D5DD' : '#344054'} />
                  }
                >
                  {isSmallScreen ? '' : 'Next'}
                </Secondary>
              </Grid>
            </Grid>
          )}
        </Grid>
      ) : emptyState ? (
        // A caller-supplied empty state (NoDataV2) wins over the legacy <NoData />,
        // so listings can migrate one at a time. (With `emptyInBody` the empty state
        // renders INSIDE the table shell above instead of here.)
        emptyState
      ) : (
        <NoData
          Actions={Actions}
          sx={{ backgroundColor: theme?.palette.customColors?.whiteBG, ...sx }}
          title={noDataTitle}
          subTitle={noDataSubTitle}
          placeHolderImage={noDataPlaceHolderImage}
          placeHolderSx={noDataPlaceHolderSx}
        />
      )}
    </>
  );
};

export default AsyncTableListing;
